import React, { useEffect, createElement } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { ContactSection } from '../components/ContactSection';
import { motion } from 'framer-motion';
import { Waves, Clock, Users, MapPin, ShieldCheck, Camera, Sun, Anchor, CheckCircle2, Package } from 'lucide-react';
import { Button } from '../components/ui/Button';
const highlights = [{
  icon: ShieldCheck,
  title: 'הדרכה צמודה',
  description: 'מדריך מוסמך ומנוסה מלווה אתכם לאורך כל הדרך'
}, {
  icon: Waves,
  title: 'חתירה רגועה',
  description: 'קצב נינוח המותאם לקבוצה, ללא מאמץ מיותר'
}, {
  icon: Camera,
  title: 'נופים מרהיבים',
  description: 'הזדמנות לראות את הכנרת מזווית אחרת ושקטה'
}, {
  icon: Package,
  title: 'הכל כלול',
  description: 'גלשן סאפ איכותי, משוט, חגורת הצלה והדרכה'
}];
const audience = ['משפחות המחפשות חוויה משותפת', 'זוגות ביום כיף רומנטי', 'קבוצות חברים', 'מי שרוצה לנסות סאפ לראשונה בסביבה בטוחה', 'מי שמחפש שקט, טבע וחיבור למים'];
export function SUPToursPage() {
  useEffect(() => {
    // SEO Meta Tags
    document.title = "טיולי סאפ מודרכים בכנרת | ביץ' קלאב מעגן";
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'טיולי סאפ מודרכים בכנרת - חוויה מרגיעה של שעה וחצי, מתאימה לכולם, ללא צורך בניסיון קודם. כולל ציוד והדרכה צמודה.');
    // Open Graph with image
    const ogTags = [{
      property: 'og:title',
      content: "טיולי סאפ מודרכים בכנרת | ביץ' קלאב מעגן"
    }, {
      property: 'og:description',
      content: 'טיולי סאפ מודרכים בכנרת - חוויה מרגיעה וקסומה לכל המשפחה.'
    }, {
      property: 'og:type',
      content: 'website'
    }, {
      property: 'og:image',
      content: 'https://cdn.magicpatterns.com/uploads/1FrmywPxsyJQJu2FnAtQFR/IMG-20251203-WA0011.jpg'
    }, {
      property: 'og:image:width',
      content: '1200'
    }, {
      property: 'og:image:height',
      content: '630'
    }, {
      property: 'og:url',
      content: window.location.href
    },
    // Twitter Card tags
    {
      property: 'twitter:card',
      content: 'summary_large_image'
    }, {
      property: 'twitter:title',
      content: "טיולי סאפ מודרכים בכנרת | ביץ' קלאב מעגן"
    }, {
      property: 'twitter:description',
      content: 'חוויה מרגיעה של שעה וחצי על מי הכנרת'
    }, {
      property: 'twitter:image',
      content: 'https://cdn.magicpatterns.com/uploads/1FrmywPxsyJQJu2FnAtQFR/IMG-20251203-WA0011.jpg'
    }];
    ogTags.forEach(tag => {
      let metaTag = document.querySelector(`meta[property="${tag.property}"]`);
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('property', tag.property);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', tag.content);
    });
  }, []);
  return <div className="min-h-screen bg-white" dir="rtl" data-id="element-1606">
      <Navigation data-id="element-1607" />

      {/* Hero Section */}
      <div className="relative h-[60vh] overflow-hidden" data-id="element-1608">
        <div className="absolute inset-0 bg-black/40 z-10" data-id="element-1609" />
        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/hhg6LtFE9yaLy4GHP6mfvz/20240328_164359.jpg" alt="SUP Tour Kinneret" className="w-full h-full object-cover" data-id="element-1610" />
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-center text-white px-4" data-id="element-1611">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-5xl md:text-7xl font-bold mb-4" data-id="element-1612">
            טיולי סאפ מודרכים
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="text-xl md:text-2xl max-w-2xl font-light" data-id="element-1613">
            לגלות את הכנרת בקצב שלכם - חוויה שקטה ומרגיעה על המים
          </motion.p>
        </div>
      </div>

      {/* Quick Info Bar */}
      <div className="bg-[#0A5F8F] text-white py-8 relative z-30 -mt-8 mx-4 md:mx-auto max-w-5xl rounded-2xl shadow-xl" data-id="element-1614">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center divide-x divide-white/20 divide-x-reverse" data-id="element-1615">
          <div className="flex flex-col items-center gap-2" data-id="element-1616">
            <Clock className="w-6 h-6 text-cyan-300" data-id="element-1617" />
            <span className="font-bold text-lg" data-id="element-1618">משך הטיול</span>
            <span className="text-blue-100" data-id="element-1619">כשעה וחצי</span>
          </div>
          <div className="flex flex-col items-center gap-2" data-id="element-1620">
            <Users className="w-6 h-6 text-cyan-300" data-id="element-1621" />
            <span className="font-bold text-lg" data-id="element-1622">למי מתאים?</span>
            <span className="text-blue-100" data-id="element-1623">לרוב האנשים</span>
          </div>
          <div className="flex flex-col items-center gap-2" data-id="element-1624">
            <ShieldCheck className="w-6 h-6 text-cyan-300" data-id="element-1625" />
            <span className="font-bold text-lg" data-id="element-1626">ניסיון קודם</span>
            <span className="text-blue-100" data-id="element-1627">לא נדרש</span>
          </div>
          <div className="flex flex-col items-center gap-2" data-id="element-1628">
            <Package className="w-6 h-6 text-cyan-300" data-id="element-1629" />
            <span className="font-bold text-lg" data-id="element-1630">מה כלול?</span>
            <span className="text-blue-100" data-id="element-1631">ציוד מלא + הדרכה</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-20" data-id="element-1632">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20" data-id="element-1633">
          <motion.div initial={{
          opacity: 0,
          x: 50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} data-id="element-1634">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0A5F8F] mb-6" data-id="element-1635">
              חוויה בלתי נשכחת על המים
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-6" data-id="element-1636">
              טיולי הסאפ שלנו מציעים הזדמנות ייחודית להתנתק מהרעש של היומיום
              ולהתחבר לשקט של הכנרת. אנחנו יוצאים בקבוצות קטנות ואינטימיות,
              מלווים במדריך מוסמך שדואג לכל פרט.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-8" data-id="element-1637">
              הטיול מתחיל בהדרכה קצרה על החוף, בה נלמד את עקרונות החתירה
              הבסיסיים, עמידה נכונה ובטיחות. לאחר מכן נצא למים לחתירה רגועה
              לאורך החוף, נהנה מהנוף, מהאוויר ומהשלווה.
            </p>

            <Button size="lg" onClick={() => document.getElementById('contact')?.scrollIntoView({
            behavior: 'smooth'
          })} data-id="element-1638">
              הזמינו טיול עכשיו
            </Button>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: -50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="relative" data-id="element-1639">
            <img src="https://cdn.magicpatterns.com/uploads/1FrmywPxsyJQJu2FnAtQFR/IMG-20251203-WA0011.jpg" alt="טיול סאפ בכנרת" className="w-full h-[400px] object-cover rounded-3xl shadow-xl" data-id="element-1640" />
          </motion.div>
        </div>

        {/* Highlights Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-20" data-id="element-1641">
          {highlights.map((item, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className="bg-blue-50 p-6 rounded-2xl border border-blue-100 hover:shadow-md transition-shadow" data-id="element-1642">
              <item.icon className="w-10 h-10 text-[#0A5F8F] mb-4" data-id="element-1643" />
              <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1644">
                {item.title}
              </h3>
              <p className="text-gray-600 text-sm" data-id="element-1645">{item.description}</p>
            </motion.div>)}
        </div>

        {/* Who is this for Section */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-3xl p-10 md:p-16 mb-20" data-id="element-1646">
          <div className="text-center mb-12" data-id="element-1647">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0A5F8F] mb-4" data-id="element-1648">
              למי הטיול מתאים?
            </h2>
            <p className="text-xl text-gray-600" data-id="element-1649">התשובה הקצרה: כמעט לכולם!</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto" data-id="element-1650">
            {audience.map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white p-6 rounded-xl shadow-sm flex items-center gap-4" data-id="element-1651">
                <div className="bg-green-100 p-2 rounded-full shrink-0" data-id="element-1652">
                  <CheckCircle2 className="w-5 h-5 text-green-600" data-id="element-1653" />
                </div>
                <span className="text-lg font-medium text-gray-800" data-id="element-1654">
                  {item}
                </span>
              </motion.div>)}
          </div>
        </motion.div>

        {/* What to bring */}
        <div className="max-w-4xl mx-auto text-center mb-20" data-id="element-1655">
          <h2 className="text-3xl font-bold text-[#0A5F8F] mb-8" data-id="element-1656">
            מה צריך להביא?
          </h2>
          <div className="flex flex-wrap justify-center gap-8" data-id="element-1657">
            {[{
            icon: Sun,
            text: 'קרם הגנה'
          }, {
            icon: Anchor,
            text: 'בגד ים'
          }, {
            icon: Package,
            text: 'מגבת'
          }, {
            icon: Camera,
            text: 'מצלמה (אופציונלי)'
          }].map((item, index) => <div key={index} className="flex flex-col items-center gap-3" data-id="element-1658">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center" data-id="element-1659">
                  <item.icon className="w-8 h-8 text-gray-600" data-id="element-1660" />
                </div>
                <span className="font-medium text-gray-700" data-id="element-1661">{item.text}</span>
              </div>)}
          </div>
        </div>

        {/* CTA Box */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-[#0A5F8F] rounded-3xl p-12 text-center text-white relative overflow-hidden" data-id="element-1662">
          <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-1663"></div>
          <div className="relative z-10" data-id="element-1664">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1665">
              מוכנים לטיול בלתי נשכח?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto" data-id="element-1666">
              הצטרפו אלינו לחוויה קסומה על המים. מספר המקומות בכל טיול מוגבל כדי
              לשמור על יחס אישי.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1667">
              <Button size="lg" className="bg-white text-[#0A5F8F] hover:bg-blue-50 border-none shadow-lg" onClick={() => document.getElementById('contact')?.scrollIntoView({
              behavior: 'smooth'
            })} data-id="element-1668">
                <span className="text-[#0A5F8F] font-bold" data-id="element-1669">
                  שריינו מקום עכשיו
                </span>
              </Button>
              <Button variant="outline" size="lg" className="border-2 border-white text-white hover:bg-white/10 backdrop-blur-sm" onClick={() => window.location.href = 'tel:0522632784'} data-id="element-1670">
                חייגו לפרטים: 052-2632784
              </Button>
            </div>
          </div>
        </motion.div>
      </div>

      <ContactSection data-id="element-1671" />
      <Footer data-id="element-1672" />
    </div>;
}